import { useState } from "react";
import {
  X,
  Users,
  Video,
  MessageCircle,
  Sparkles,
  Calendar,
  BookOpen,
  Check,
  ArrowRight,
  Loader2,
  Heart,
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const SkoolInfoModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");

  const features = [
    {
      icon: Video,
      title: "Weekly Live Coaching Calls",
      description: "Join me live every week for coaching, Q&A, and community connection — replays always available.",
    },
    {
      icon: BookOpen,
      title: "Practical Education Library",
      description: "New lessons every week on peptides, nutrition, hormones, sleep, and the nervous system.",
    },
    {
      icon: MessageCircle,
      title: "Expert Q&As",
      description: "Deep dives on peptide protocols, blood work interpretation, and hot topics from our community.",
    },
    {
      icon: Users,
      title: "Private Sisterhood",
      description: "A community of high-achieving women walking this journey together. Support, friendship, real conversation.",
    },
    {
      icon: Sparkles,
      title: "Actionable Tools",
      description: "Downloadable protocols, meal templates, tracking sheets — everything you need to implement.",
    },
    {
      icon: Calendar,
      title: "Cancel Anytime",
      description: "No contracts, no long-term commitment. Stay because it works, not because you're locked in.",
    },
  ];

  const handleJoin = async () => {
    const buyerEmail = (user?.email || email || "").trim();
    const buyerName = user?.name || name || "";
    if (!buyerEmail) {
      toast.error("Please enter your email so we can send you the welcome kit.");
      return;
    }
    setIsCheckingOut(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: "decoded_monthly",
        origin_url: originUrl,
        email: buyerEmail,
        name: buyerName,
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Unable to start checkout. Please try again.");
      setIsCheckingOut(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="skool-info-modal">
      <div
        className="bg-[#F6F4EF] rounded-3xl max-w-3xl w-full max-h-[92vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero header */}
        <div className="relative bg-[#1F2818] text-white p-8 md:p-10 rounded-t-3xl">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="skool-modal-close"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#B89660] flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" strokeWidth={1.5} fill="currentColor" />
            </div>
            <span className="overline text-[#DCC286]">The Membership</span>
          </div>

          <h2 className="font-heading text-3xl md:text-5xl leading-[1.1] mb-4">
            Welcome to <span className="italic text-[#DCC286]">Perimenopause Decoded</span>
          </h2>

          <p className="text-white/85 font-light leading-relaxed max-w-xl md:text-lg">
            Understand your body. Reclaim your health. Thrive through perimenopause —
            with education, live coaching, and a community of women walking the same path.
          </p>

          <div className="mt-6 flex items-baseline gap-3">
            <span className="font-heading text-5xl text-white">$47</span>
            <span className="text-white/70 text-sm">/ month • cancel anytime</span>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 md:p-10">
          {/* What is Skool */}
          <div className="mb-10">
            <span className="overline mb-3 block">What is Skool?</span>
            <p className="text-[#4A5543] font-light leading-relaxed md:text-lg">
              Skool is the private platform where our community lives. Think of it as our
              private club — courses, weekly calls, discussions, and direct access to me — all
              in one clean, distraction-free space. Available on web and mobile app.
            </p>
          </div>

          {/* What's inside */}
          <div className="mb-10">
            <span className="overline mb-4 block">What's inside the membership</span>
            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((feature, i) => (
                <div
                  key={i}
                  className="flex gap-4 p-4 rounded-2xl bg-white border border-[#E3DAC9]"
                  data-testid={`skool-feature-${i}`}
                >
                  <div className="w-10 h-10 rounded-full bg-[#B89660]/10 flex items-center justify-center flex-shrink-0">
                    <feature.icon className="w-5 h-5 text-[#B89660]" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-heading text-lg text-[#1F2818] leading-tight mb-1">
                      {feature.title}
                    </h4>
                    <p className="text-[#4A5543] font-light text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Perfect for section */}
          <div className="mb-10 rounded-2xl bg-white border border-[#E3DAC9] p-6 md:p-8">
            <span className="overline mb-3 block">Perfect for you if...</span>
            <ul className="space-y-3">
              {[
                "You want to understand what's happening in your body instead of guessing",
                "You're tired of conflicting advice from books, podcasts, and Instagram",
                "You want community — women who get what you're going through",
                "You're curious about peptides, hormones, and nutrition science — done safely",
                "You want education without a big financial commitment yet",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <Check
                    className="w-5 h-5 text-[#B89660] flex-shrink-0 mt-0.5"
                    strokeWidth={2.5}
                  />
                  <span className="text-[#4A5543] font-light leading-relaxed">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Ivana's promise */}
          <div className="mb-10 border-l-2 border-[#B89660] pl-6">
            <p className="font-heading text-xl md:text-2xl text-[#1F2818] leading-relaxed italic mb-3">
              "This community is my life's work distilled into one accessible place.
              Whether you're just starting or deep in your journey — you belong here."
            </p>
            <p className="text-[#4A5543] text-sm tracking-[0.15em] uppercase">
              — Ivana Silva
            </p>
          </div>

          {/* Final CTA — collect email so Kit welcome sequence can fire */}
          {!user && (
            <div className="grid gap-3 mb-4 max-w-md mx-auto">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your first name"
                className="w-full px-4 py-3 rounded-full border border-[#E3DAC9] bg-[#F9F8F6] text-[#2C3524] placeholder:text-[#4A5543]/50 focus:outline-none focus:border-[#C5A059] transition-colors"
                data-testid="skool-name-input"
              />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your best email"
                className="w-full px-4 py-3 rounded-full border border-[#E3DAC9] bg-[#F9F8F6] text-[#2C3524] placeholder:text-[#4A5543]/50 focus:outline-none focus:border-[#C5A059] transition-colors"
                data-testid="skool-email-input"
              />
            </div>
          )}
          <button
            onClick={handleJoin}
            disabled={isCheckingOut}
            className="w-full btn-primary text-base group"
            data-testid="skool-join-cta"
          >
            {isCheckingOut ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Redirecting to checkout...
              </>
            ) : (
              <>
                Join for $47/month
                <ArrowRight className="inline-block ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </>
            )}
          </button>
          <p className="text-xs text-[#4A5543]/60 text-center mt-3">
            Cancel anytime • Secure checkout via Stripe
          </p>
        </div>
      </div>
    </div>
  );
};

export default SkoolInfoModal;
